import { Capacitor } from '@capacitor/core';
import { api } from '../lib/api';

// Web-safe native bridge. Every export is a no-op on the web (where
// Capacitor.isNativePlatform() === false). On iOS/Android these wire up push
// registration + deep-link handling against the active tenant.

let registeredToken: string | null = null;
let listenersBound = false;

type NavigateFn = (path: string) => void;

function defaultNavigate(path: string) {
  try {
    window.location.assign(path);
  } catch {
    /* ignore */
  }
}

/**
 * Request notification permission, register for push, and POST the device
 * token to the active tenant. Tapping a notification deep-links to the chat.
 * No-op on web.
 */
export async function registerPush(tenantCode: string, navigate: NavigateFn = defaultNavigate): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');

    if (!listenersBound) {
      listenersBound = true;

      await PushNotifications.addListener('registration', (token) => {
        registeredToken = token.value;
        const platform = Capacitor.getPlatform();
        api.registerDevice(tenantCode, token.value, platform).catch(() => {});
      });

      await PushNotifications.addListener('registrationError', () => {
        /* swallow — push is best-effort */
      });

      await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
        const data = (action.notification?.data ?? {}) as { chatId?: string };
        if (data.chatId) navigate(`/inbox/${data.chatId}`);
      });
    }

    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
      perm = await PushNotifications.requestPermissions();
    }
    if (perm.receive !== 'granted') return;

    await PushNotifications.register();
  } catch {
    // plugin missing / native error — ignore so the web build is unaffected
  }
}

/** Best-effort de-registration of the current device token. No-op on web. */
export async function unregisterPush(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  if (!registeredToken) return;
  const token = registeredToken;
  registeredToken = null;
  try {
    await api.unregisterDevice(token);
  } catch {
    /* ignore */
  }
}

/**
 * Optional helper: invoke a callback when the native app moves to background /
 * foreground, so callers can pause/resume presence heartbeats. No-op on web.
 * Returns a cleanup function.
 */
export async function onAppStateChange(
  cb: (active: boolean) => void
): Promise<() => void> {
  if (!Capacitor.isNativePlatform()) return () => {};
  try {
    const { App } = await import('@capacitor/app');
    const handle = await App.addListener('appStateChange', (state) => {
      cb(state.isActive);
    });
    return () => {
      handle.remove().catch(() => {});
    };
  } catch {
    return () => {};
  }
}
